import React, { useEffect, useState, useCallback } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  Alert,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from '../../store';
import {
  selectActiveNotifications,
  selectNotificationOptimization,
  selectNotificationLoading,
  selectNotificationError,
  selectEngagementPatterns,
  selectTimingPreferences,
  selectQuietModeActive,
  fetchNotificationOptimization,
  fetchEngagementPatterns,
  fetchTimingPreferences,
  recordNotificationEngagement,
  setQuietModeActive,
  removeActiveNotification,
} from '../../store/slices/notificationIntelligenceSlice';
import type { SmartNotification } from '@coffeeplexity/shared';
import { SmartNotificationComponent } from './SmartNotification';
import { NotificationOptimizationDisplay } from './NotificationOptimizationDisplay';
import { NotificationPreferencesPanel } from './NotificationPreferencesPanel';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import { styles } from './IntelligentNotificationCenter.styles';
import { logger } from '../../utils/logger';

interface IntelligentNotificationCenterProps {
  userId: string;
  onNotificationPress?: (notification: SmartNotification) => void;
  onSettingsPress?: () => void;
}

export const IntelligentNotificationCenter: React.FC<
  IntelligentNotificationCenterProps
> = ({ userId, onNotificationPress, onSettingsPress }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [refreshing, setRefreshing] = useState(false);
  const [showOptimization, setShowOptimization] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  // Selectors
  const activeNotifications = useSelector(selectActiveNotifications);
  const optimizationSettings = useSelector(selectNotificationOptimization);
  const loading = useSelector(selectNotificationLoading);
  const error = useSelector(selectNotificationError);
  const engagementPatterns = useSelector(selectEngagementPatterns);
  const timingPreferences = useSelector(selectTimingPreferences);
  const quietModeActive = useSelector(selectQuietModeActive);

  // Load initial data
  useEffect(() => {
    if (userId) {
      dispatch(fetchNotificationOptimization(userId));
      dispatch(fetchEngagementPatterns(userId));
      dispatch(fetchTimingPreferences(userId));
    }
  }, [dispatch, userId]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        dispatch(fetchNotificationOptimization(userId)).unwrap(),
        dispatch(fetchEngagementPatterns(userId)).unwrap(),
        dispatch(fetchTimingPreferences(userId)).unwrap(),
      ]);
    } catch (error) {
      logger.error('Failed to refresh notification data:', error);
    } finally {
      setRefreshing(false);
    }
  }, [dispatch, userId]);

  // Handle notification interaction
  const handleNotificationPress = useCallback(
    (notification: SmartNotification) => {
      // Record engagement
      dispatch(
        recordNotificationEngagement({
          notificationId: notification.id,
          engagementType: 'clicked',
          context: {
            source: 'notification_center',
            timestamp: new Date().toISOString(),
          },
        })
      );

      // Call external handler
      onNotificationPress?.(notification);
    },
    [dispatch, onNotificationPress]
  );

  // Handle notification dismissal
  const handleNotificationDismiss = useCallback(
    (notification: SmartNotification) => {
      // Record engagement
      dispatch(
        recordNotificationEngagement({
          notificationId: notification.id,
          engagementType: 'dismissed',
          context: {
            source: 'notification_center',
            timestamp: new Date().toISOString(),
          },
        })
      );

      // Remove from active notifications
      dispatch(removeActiveNotification(notification.id));
    },
    [dispatch]
  );

  // Handle quiet mode toggle
  const handleQuietModeToggle = useCallback(() => {
    dispatch(setQuietModeActive(!quietModeActive));
  }, [dispatch, quietModeActive]);

  // Calculate optimization score
  const optimizationScore = optimizationSettings
    ? Math.round(
        (optimizationSettings.engagementThreshold +
          optimizationSettings.adaptationRate) *
          50
      )
    : 0;

  // Group notifications by priority
  const groupedNotifications = activeNotifications.reduce(
    (acc, notification) => {
      if (!acc[notification.priority]) {
        acc[notification.priority] = [];
      }
      acc[notification.priority].push(notification);
      return acc;
    },
    {} as Record<string, SmartNotification[]>
  );

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorTitle}>Unable to Load Notifications</Text>
        <Text style={styles.errorMessage}>
          {error.message || 'Something went wrong'}
        </Text>
        <TouchableOpacity style={styles.retryButton} onPress={handleRefresh}>
          <Text style={styles.retryButtonText}>Try Again</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Text style={styles.title}>Smart Notifications</Text>
          <View style={styles.optimizationBadge}>
            <ConfidenceIndicator
              confidence={optimizationScore / 100}
              size='small'
              showLabel={false}
            />
            <Text style={styles.optimizationText}>
              {optimizationScore}% Optimized
            </Text>
          </View>
        </View>
        <View style={styles.headerRight}>
          <TouchableOpacity
            style={[
              styles.quietModeButton,
              quietModeActive && styles.quietModeButtonActive,
            ]}
            onPress={handleQuietModeToggle}
          >
            <Text
              style={[
                styles.quietModeButtonText,
                quietModeActive && styles.quietModeButtonTextActive,
              ]}
            >
              {quietModeActive ? '🔕' : '🔔'}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.settingsButton}
            onPress={() => setShowOptimization(!showOptimization)}
          >
            <Text style={styles.settingsButtonText}>⚙️</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* AI Optimization Display */}
      {showOptimization && optimizationSettings && (
        <NotificationOptimizationDisplay
          optimization={optimizationSettings}
          engagementPatterns={engagementPatterns}
          timingPreferences={timingPreferences}
          onClose={() => setShowOptimization(false)}
          onPreferencesPress={() => setShowPreferences(true)}
        />
      )}

      {/* Preferences Panel */}
      {showPreferences && (
        <NotificationPreferencesPanel
          userId={userId}
          onClose={() => setShowPreferences(false)}
        />
      )}

      {/* Notifications List */}
      <ScrollView
        style={styles.scrollView}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
        }
        showsVerticalScrollIndicator={false}
      >
        {activeNotifications.length === 0 ? (
          <View style={styles.emptyState}>
            <Text style={styles.emptyStateIcon}>🎯</Text>
            <Text style={styles.emptyStateTitle}>All Caught Up!</Text>
            <Text style={styles.emptyStateMessage}>
              Your AI is learning your preferences to send you the most relevant
              notifications at the perfect time.
            </Text>
          </View>
        ) : (
          <>
            {/* High Priority Notifications */}
            {groupedNotifications.high && (
              <View style={styles.prioritySection}>
                <Text style={styles.prioritySectionTitle}>High Priority</Text>
                {groupedNotifications.high.map(notification => (
                  <SmartNotificationComponent
                    key={notification.id}
                    notification={notification}
                    onPress={() => handleNotificationPress(notification)}
                    onDismiss={() => handleNotificationDismiss(notification)}
                    showAIReasoning={true}
                  />
                ))}
              </View>
            )}

            {/* Medium Priority Notifications */}
            {groupedNotifications.medium && (
              <View style={styles.prioritySection}>
                <Text style={styles.prioritySectionTitle}>Recommended</Text>
                {groupedNotifications.medium.map(notification => (
                  <SmartNotificationComponent
                    key={notification.id}
                    notification={notification}
                    onPress={() => handleNotificationPress(notification)}
                    onDismiss={() => handleNotificationDismiss(notification)}
                    showAIReasoning={false}
                  />
                ))}
              </View>
            )}

            {/* Low Priority Notifications */}
            {groupedNotifications.low && (
              <View style={styles.prioritySection}>
                <Text style={styles.prioritySectionTitle}>Later</Text>
                {groupedNotifications.low.map(notification => (
                  <SmartNotificationComponent
                    key={notification.id}
                    notification={notification}
                    onPress={() => handleNotificationPress(notification)}
                    onDismiss={() => handleNotificationDismiss(notification)}
                    showAIReasoning={false}
                  />
                ))}
              </View>
            )}
          </>
        )}

        {/* Loading indicator */}
        {loading.notifications && (
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>
              AI is optimizing your notifications...
            </Text>
          </View>
        )}
      </ScrollView>
    </View>
  );
};
